import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, cn } from '@shared/utils';
import { type AccountResponse, type ProfileResponse, type UserResponse, type UserWithPaymentInfo } from '@shared/api';
import { AccountsAndCardsPicker } from '../accountsAndCardsPicker';
import { Button, Modal } from '@shared/widgets';

interface FormData {
  accounts: number[];
}
const DEFAULT_VALUES: FormData = { accounts: [] };

interface AddAccountsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UserResponse | ProfileResponse;
  accountsItems: AccountResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; accountIds: number[] }) => Promise<unknown>;
}

export const AddAccountsModal = (props: AddAccountsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);

  const methods = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const { formState, handleSubmit, reset } = methods;
  const { t } = useTranslation();

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: FormData) => {
    await props.onSubmit({ userIds: props.user.id, accountIds: data.accounts });
    closeHandler();
  };

  const accounts = useMemo(() => {
    if (props.isOpen && profileFeatureStore.data?.accountFlows)
      return AccountUtils.filterAccounts(profileFeatureStore.data?.accountFlows ?? null, userAccountsStore.data)
        .filter((account) => account.mainAccId == null)
        .filter((account) => !props.accountsItems.map((accountItem) => accountItem.id).includes(account.id));
    return [];
  }, [props.isOpen, profileFeatureStore.data?.accountFlows, userAccountsStore.data]);
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.addAccounts.title')}>
      <FormProvider {...methods}>
        <form className={cn('flex w-[640px] flex-col gap-6', 'max-md:w-[308px]')} noValidate onSubmit={handleSubmit(submitHandler)}>
          <AccountsAndCardsPicker accounts={accounts} noCards />
          <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('buttons.add')}
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};
